import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Inbox, Search } from "lucide-react";
import { getAllDocs } from "../../Firebase/Firebase.utils";
import PressCoverageReviewQueue from "../components/PressCoverageReviewQueue";
import PressCoverageSearch from "../components/PressCoverageSearch";

// CMS home for the Press Coverage feature.
//
// Two tabs:
//   1. Review Queue — the `newsRss` collection grouped by status
//      (Pending / Approved / Rejected). The daily cron drops fresh
//      Leechiu mentions here as Pending; ops approves or rejects with
//      bulk actions. Rejected items stay as tombstones so the cron
//      doesn't re-add the same URL on its next run.
//   2. Search RSS — ad-hoc live fetch through nextjsprojv1's
//      /api/rss-search proxy. Useful for backfilling older mentions
//      the cron has already collected — anything added here goes
//      straight to Approved (human just picked it, no review needed).
//
// The collection load lives at this page level so both tabs share the
// same `existingHashes` set — that's how the Search tab knows which
// results are already in the DB and dims them with a ✓ Added badge.
export default function PressCoverage() {
  const [tab, setTab] = useState("queue");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await getAllDocs("newsRss");
      setDocs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[press-coverage] failed to load newsRss:", err);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const existingHashes = useMemo(() => {
    const s = new Set();
    for (const d of docs) if (d?.id) s.add(d.id);
    return s;
  }, [docs]);

  const handleAdded = useCallback(() => {
    // A card was just added in the Search tab — quickly refetch so the
    // Added tab count/list is fresh next time the user switches to it.
    load();
  }, [load]);

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="cms-eyebrow mb-1.5">Public /news</div>
          <h1>Press Coverage</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl">
            A daily cron scans Philippine business RSS feeds and drops
            Leechiu mentions into the Review Queue as Pending. Approve or
            reject in bulk. Approved items appear on the public /news page;
            rejected items stay as tombstones so the same article never
            re-appears on the next daily run.
          </p>
        </div>
      </div>

      <div className="mb-5 flex items-center gap-1 border-b border-gray-200">
        <TabButton
          active={tab === "queue"}
          onClick={() => setTab("queue")}
          icon={<Inbox className="w-4 h-4" />}
          label="Review Queue"
          count={docs.length}
        />
        <TabButton
          active={tab === "search"}
          onClick={() => setTab("search")}
          icon={<Search className="w-4 h-4" />}
          label="Search RSS"
        />
      </div>

      {tab === "queue" ? (
        <PressCoverageReviewQueue
          docs={docs}
          loading={loading}
          onChanged={load}
        />
      ) : (
        <PressCoverageSearch
          existingHashes={existingHashes}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-leechiuOrange text-leechiuBlue"
          : "border-transparent text-gray-500 hover:text-leechiuBlue"
      }`}
    >
      {icon}
      {label}
      {typeof count === "number" ? (
        <span
          className={`inline-flex items-center justify-center rounded-full text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px] ${
            active
              ? "bg-leechiuOrange/10 text-leechiuOrange"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}
